import React, { useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LayersIcon, XIcon, ArrowRightIcon, SparklesIcon } from 'lucide-react';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { useRoleTheme } from '../../../hooks/useRoleTheme';
import { usePlatform } from '../../../context/PlatformContext';
import { storyGalaxyData } from '../../../data/storyGalaxyData';
interface StoryStackBarProps {
  selectedTags: string[];
  onRemoveTag: (tag: string) => void;
  onClearAll: () => void;
  onAddTag?: (tag: string) => void;
  onExplore?: () => void;
}
export const StoryStackBar: React.FC<StoryStackBarProps> = ({
  selectedTags,
  onRemoveTag,
  onClearAll,
  onAddTag,
  onExplore
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    isAfterTide
  } = usePlatform();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const accentBg = isAfterTide ? 'bg-[#e2b04a]' : 'bg-indigo-600';
  const accentText = isAfterTide ? 'text-[#1a1a2e]' : 'text-white';
  const accentIcon = isAfterTide ? 'text-[#e2b04a]' : 'text-indigo-500';
  const handleAddStack = (stack: SuggestedStoryStack) => {
    if (!onAddTag) return;
    stack.nodeIds.forEach(id => {
      const node = getNodeById(id);
      if (node && !selectedTags.includes(node.name)) {
        onAddTag(node.name);
      }
    });
  };
  // If stack is empty, show suggested stacks
  if (selectedTags.length === 0) {
    return <motion.div initial={{
      y: 100,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} className={`fixed bottom-0 left-0 right-0 z-40 border-t shadow-[0_-4px_20px_rgba(0,0,0,0.1)] ${dk.isDark ? 'bg-gray-900/95 border-gray-800' : 'bg-white/95 border-gray-200'} backdrop-blur-md pb-[env(safe-area-inset-bottom)]`} data-id="element-14103" data-mp="shiod0wtcefc">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4" data-id="element-14104" data-mp="33by2ctiutyvu">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4" data-id="element-14105" data-mp="19snzqgaj0ndw">
            <div className="flex items-center gap-2 flex-shrink-0" data-id="element-14106" data-mp="14t2vprgi1oub">
              <SparklesIcon className={`h-5 w-5 ${accentIcon}`} data-id="element-14107" data-mp="14turp3e0dol4" />
              <span className={`font-bold text-sm ${dk.textHeading}`} data-id="element-14108" data-mp="101b6bk92xp5r">
                Suggested Stacks:
              </span>
            </div>
            <div className="flex-1 flex items-center overflow-x-auto scrollbar-hide gap-2" style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }} data-id="element-14109" data-mp="v15hscy3wirw">
              {storyGalaxyData.suggestedStoryStacks.map(stack => <button key={stack.id} onClick={() => handleAddStack(stack)} className={`inline-flex items-center flex-shrink-0 px-3 py-1.5 ${rt.badgeRadius} border text-xs font-medium shadow-sm whitespace-nowrap transition-colors ${dk.isDark ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700 hover:text-white' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'}`} data-id="element-14110" data-mp="c4dtfp3pjdgz">
                  <LayersIcon className="h-3.5 w-3.5 mr-1.5 opacity-70" data-id="element-14111" data-mp="2izg4vtduz7pm" />
                  {stack.label}
                </button>)}
            </div>
          </div>
        </div>
      </motion.div>;
  }
  return <motion.div initial={{
    y: 100,
    opacity: 0
  }} animate={{
    y: 0,
    opacity: 1
  }} exit={{
    y: 100,
    opacity: 0
  }} className={`fixed bottom-0 left-0 right-0 z-40 border-t shadow-[0_-4px_20px_rgba(0,0,0,0.1)] ${dk.isDark ? 'bg-gray-900/95 border-gray-800' : 'bg-white/95 border-gray-200'} backdrop-blur-md pb-[env(safe-area-inset-bottom)]`} data-id="element-14112" data-mp="2ssoiavinyzdc">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4" data-id="element-14113" data-mp="3gh7s5fhd3k7j">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-14114" data-mp="3lfhr8167j4yf">
          {/* Left: Stack Info & Tags */}
          <div className="flex items-center gap-4 flex-1 min-w-0" data-id="element-14115" data-mp="3j1w1b9am8z85">
            <div className="flex items-center gap-2 flex-shrink-0" data-id="element-14116" data-mp="3eziv6jryizau">
              <LayersIcon className={`h-5 w-5 ${accentIcon}`} data-id="element-14117" data-mp="3f1c4k6tyfxmj" />
              <span className={`font-bold hidden sm:inline ${dk.textHeading}`} data-id="element-14118" data-mp="3t01yxepe3j77">
                Story Stack
              </span>
            </div>

            <div className="flex-1 flex items-center overflow-x-auto scrollbar-hide gap-2" style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }} data-id="element-14119" data-mp="3o2a8yq8b275n">
              <AnimatePresence mode="popLayout" data-id="element-14120" data-mp="fkdu942o3w8s">
                {selectedTags.map(tag => <motion.span key={tag} initial={{
                scale: 0.8,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} exit={{
                scale: 0.8,
                opacity: 0
              }} layout className={`inline-flex items-center flex-shrink-0 px-3 py-1.5 ${rt.badgeRadius} ${accentBg} ${accentText} text-xs font-medium shadow-sm whitespace-nowrap`} data-id="element-14121" data-mp="akrhswunyk11">
                    {tag}
                    <button onClick={() => onRemoveTag(tag)} className="ml-2 opacity-70 hover:opacity-100 focus:outline-none" data-id="element-14122" data-mp="kh1biur8zl86">
                      <XIcon className="h-3.5 w-3.5" data-id="element-14123" data-mp="bgbmqb0emv3y" />
                    </button>
                  </motion.span>)}
              </AnimatePresence>
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-3 flex-shrink-0 justify-end" data-id="element-14124" data-mp="1hvsw09zt7qr">
            <button onClick={onClearAll} className={`text-xs font-medium ${dk.textMuted} hover:text-red-500 transition-colors`} data-id="element-14125" data-mp="6gz11tl9ru2y">
              Clear
            </button>
            <button onClick={() => {
            const tagsParam = selectedTags.map(t => encodeURIComponent(t)).join(',');
            navigate(`/story-stack?tags=${tagsParam}&from=galaxy`);
          }} className={`flex items-center gap-1.5 px-5 py-2 rounded-lg font-bold text-sm transition-colors ${accentBg} ${accentText} hover:opacity-90 shadow-md`} data-id="element-14126" data-mp="3eqrgqjnvj79c">
              Find Books
              <ArrowRightIcon className="h-4 w-4" data-id="element-14127" data-mp="1ak5tpkv6kcq" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>;
};